import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { greenCoffeeLotsApi, suppliersApi, warehousesApi } from '../../../services/api'
import StatusBadge from './StatusBadge'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

interface Warehouse {
  id: number
  name: string
  is_active: boolean
}

interface Supplier {
  id: number
  name: string
  is_active: boolean
}

interface GreenCoffeeLot {
  id: number
  lot_code: string
  kg_purchased: number
  kg_remaining: number
  price_per_kg: number
  assembly_cost: number
  freight_cost: number
  storage_cost: number
  total_cost: number
  cost_per_kg: number
  status: string
  warehouse_id: number
  warehouse_name: string
  supplier_id: number | null
  supplier_name: string | null
  requested_date: string | null
  expected_arrival_date: string | null
  received_date: string | null
  notes: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

interface LotForm {
  kg_purchased: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
  warehouse_id: string
  supplier_id: string
  requested_date: string
  expected_arrival_date: string
  status: 'pending' | 'in_transit'
  notes: string
}

const emptyForm: LotForm = {
  kg_purchased: '',
  price_per_kg: '',
  assembly_cost: '',
  freight_cost: '',
  storage_cost: '',
  warehouse_id: '',
  supplier_id: '',
  requested_date: '',
  expected_arrival_date: '',
  status: 'pending',
  notes: '',
}

export default function PurchasesTab() {
  const [lots, setLots] = useState<GreenCoffeeLot[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [editingLot, setEditingLot] = useState<GreenCoffeeLot | null>(null)
  const [form, setForm] = useState<LotForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [receiveLot, setReceiveLot] = useState<GreenCoffeeLot | null>(null)
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0])
  const [receiveSaving, setReceiveSaving] = useState(false)
  const [cancelingId, setCancelingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      setLoadError('')
      const [lotsRes, warehousesRes, suppliersRes] = await Promise.all([
        greenCoffeeLotsApi.list(),
        warehousesApi.list(),
        suppliersApi.list(),
      ])
      setLots(lotsRes.data.lots || [])
      setWarehouses(warehousesRes.data.warehouses || [])
      setSuppliers(suppliersRes.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching green coffee lots:', err)
      setLoadError('No se pudieron cargar los lotes de compra. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeWarehouses = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses])

  const filteredLots = useMemo(() => {
    if (!statusFilter) return lots
    return lots.filter((l) => l.status === statusFilter)
  }, [lots, statusFilter])

  const { sortedData: sortedLots, sortConfig, requestSort } = useSortableData(filteredLots)

  const totalCostPreview =
    (parseFloat(form.kg_purchased) || 0) * (parseFloat(form.price_per_kg) || 0) +
    (parseFloat(form.assembly_cost) || 0) +
    (parseFloat(form.freight_cost) || 0) +
    (parseFloat(form.storage_cost) || 0)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingLot(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (lot: GreenCoffeeLot) => {
    setForm({
      kg_purchased: String(lot.kg_purchased),
      price_per_kg: String(lot.price_per_kg),
      assembly_cost: String(lot.assembly_cost),
      freight_cost: String(lot.freight_cost),
      storage_cost: String(lot.storage_cost),
      warehouse_id: String(lot.warehouse_id),
      supplier_id: lot.supplier_id ? String(lot.supplier_id) : '',
      requested_date: lot.requested_date || '',
      expected_arrival_date: lot.expected_arrival_date || '',
      status: lot.status === 'in_transit' ? 'in_transit' : 'pending',
      notes: lot.notes || '',
    })
    setEditingLot(lot)
    setError('')
    setShowForm(true)
  }

  const canEditCostFields = editingLot ? editingLot.kg_remaining === editingLot.kg_purchased : true

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingLot) {
      if (!form.kg_purchased || parseFloat(form.kg_purchased) <= 0) {
        setError('Ingresa un peso comprado válido')
        return
      }
      if (!form.price_per_kg || parseFloat(form.price_per_kg) < 0) {
        setError('Ingresa un precio por kg válido')
        return
      }
    }
    if (!form.warehouse_id) {
      setError('Selecciona un almacén')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (editingLot) {
        const payload: any = {
          warehouse_id: Number(form.warehouse_id),
          supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
          expected_arrival_date: form.expected_arrival_date || null,
          notes: form.notes || null,
        }
        if (canEditCostFields) {
          payload.kg_purchased = parseFloat(form.kg_purchased)
          payload.price_per_kg = parseFloat(form.price_per_kg)
          payload.assembly_cost = parseFloat(form.assembly_cost) || 0
          payload.freight_cost = parseFloat(form.freight_cost) || 0
          payload.storage_cost = parseFloat(form.storage_cost) || 0
        }
        await greenCoffeeLotsApi.update(editingLot.id, payload)
        toast.success('Lote actualizado')
      } else {
        await greenCoffeeLotsApi.create({
          kg_purchased: parseFloat(form.kg_purchased),
          price_per_kg: parseFloat(form.price_per_kg),
          assembly_cost: parseFloat(form.assembly_cost) || 0,
          freight_cost: parseFloat(form.freight_cost) || 0,
          storage_cost: parseFloat(form.storage_cost) || 0,
          warehouse_id: Number(form.warehouse_id),
          supplier_id: form.supplier_id ? Number(form.supplier_id) : undefined,
          requested_date: form.requested_date || undefined,
          expected_arrival_date: form.expected_arrival_date || undefined,
          status: form.status,
          notes: form.notes || undefined,
        })
        toast.success('Lote de compra creado')
      }
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el lote')
    } finally {
      setSaving(false)
    }
  }

  const openReceive = (lot: GreenCoffeeLot) => {
    setReceiveLot(lot)
    setReceivedDate(new Date().toISOString().split('T')[0])
  }

  const handleReceiveSubmit = async () => {
    if (!receiveLot) return
    setReceiveSaving(true)
    try {
      await greenCoffeeLotsApi.receive(receiveLot.id, { received_date: receivedDate || undefined })
      toast.success('Lote marcado como recibido en almacén')
      setReceiveLot(null)
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al marcar el lote como recibido')
    } finally {
      setReceiveSaving(false)
    }
  }

  const handleCancel = async (lot: GreenCoffeeLot) => {
    if (!confirm(`¿Anular la compra ${lot.lot_code}? Esta acción restaurará los kilos reservados en la etapa anterior.`)) return
    setCancelingId(lot.id)
    try {
      await greenCoffeeLotsApi.cancel(lot.id)
      toast.success('Lote de compra anulado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al anular el lote')
    } finally {
      setCancelingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="purchase-status-filter" className="text-xs font-medium text-espresso-500">
            Estado
          </label>
          <select
            id="purchase-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="in_transit">En Tránsito</option>
            <option value="at_rest">En Almacén</option>
            <option value="consumed">Consumido</option>
          </select>
        </div>
        <button
          onClick={openNew}
          disabled={activeWarehouses.length === 0}
          title={activeWarehouses.length === 0 ? 'Crea primero un almacén activo en la pestaña Almacenes' : undefined}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nueva Compra
        </button>
      </div>

      {activeWarehouses.length === 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          No hay almacenes activos. Crea uno en la pestaña "Almacenes" antes de registrar una compra.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && filteredLots.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">
            {lots.length === 0 ? 'No hay compras registradas aún.' : 'No hay lotes con este estado.'}
          </p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Lote" sortKey="lot_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Fecha" sortKey="requested_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Almacén" sortKey="warehouse_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Proveedor" sortKey="supplier_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Comprados" sortKey="kg_purchased" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Restantes" sortKey="kg_remaining" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo/kg" sortKey="cost_per_kg" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo Total" sortKey="total_cost" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedLots.map((lot) => (
                  <tr key={lot.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{lot.lot_code}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {lot.requested_date ? new Date(lot.requested_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{lot.warehouse_name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{lot.supplier_name || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(lot.kg_purchased).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(lot.kg_remaining).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">${Number(lot.cost_per_kg).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">${Number(lot.total_cost).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap"><StatusBadge status={lot.status} isActive={lot.is_active} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(lot)} variant="primary">
                          Editar
                        </TableActionButton>
                        {(lot.status === 'pending' || lot.status === 'in_transit') && (
                          <TableActionButton onClick={() => openReceive(lot)} variant="success">
                            Recibir
                          </TableActionButton>
                        )}
                        {lot.is_active !== false && (
                          <TableActionButton
                            onClick={() => handleCancel(lot)}
                            variant="danger"
                            disabled={cancelingId === lot.id || Number(lot.kg_remaining) !== Number(lot.kg_purchased)}
                            title={
                              Number(lot.kg_remaining) !== Number(lot.kg_purchased)
                                ? 'No se puede anular: ya se generaron lotes de selección a partir de este lote'
                                : undefined
                            }
                          >
                            {cancelingId === lot.id ? 'Anulando...' : 'Anular'}
                          </TableActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingLot ? `Editar Lote ${editingLot.lot_code}` : 'Nueva Compra de Café Verde'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              {editingLot && !canEditCostFields && (
                <div className="bg-cream-100 text-espresso-700 px-4 py-2 rounded text-sm">
                  Este lote ya tiene kg consumidos, por lo que el peso y los costos no se pueden editar.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="lot-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                    Kg Comprados *
                  </label>
                  <input
                    id="lot-kg"
                    type="number"
                    name="kg_purchased"
                    value={form.kg_purchased}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={!!editingLot && !canEditCostFields}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lot-price" className="block text-sm font-medium text-espresso-700 mb-1">
                    Precio / kg *
                  </label>
                  <input
                    id="lot-price"
                    type="number"
                    name="price_per_kg"
                    value={form.price_per_kg}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={!!editingLot && !canEditCostFields}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="lot-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                    Acopio
                  </label>
                  <input
                    id="lot-assembly"
                    type="number"
                    name="assembly_cost"
                    value={form.assembly_cost}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={!!editingLot && !canEditCostFields}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                  />
                </div>
                <div>
                  <label htmlFor="lot-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                    Flete
                  </label>
                  <input
                    id="lot-freight"
                    type="number"
                    name="freight_cost"
                    value={form.freight_cost}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={!!editingLot && !canEditCostFields}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                  />
                </div>
                <div>
                  <label htmlFor="lot-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                    Almacenaje
                  </label>
                  <input
                    id="lot-storage"
                    type="number"
                    name="storage_cost"
                    value={form.storage_cost}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={!!editingLot && !canEditCostFields}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                  />
                </div>
              </div>
              {!editingLot && (
                <div className="bg-cream-50 rounded-md p-3 text-sm flex justify-between font-semibold">
                  <span>Costo total estimado:</span>
                  <span>${totalCostPreview.toFixed(2)}</span>
                </div>
              )}
              <div>
                <label htmlFor="lot-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén de destino *
                </label>
                <select
                  id="lot-warehouse"
                  name="warehouse_id"
                  value={form.warehouse_id}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="">Selecciona un almacén</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id} disabled={!w.is_active}>
                      {w.name}{!w.is_active ? ' (inactivo)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="lot-supplier" className="block text-sm font-medium text-espresso-700 mb-1">
                  Proveedor
                </label>
                <select
                  id="lot-supplier"
                  name="supplier_id"
                  value={form.supplier_id}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                >
                  <option value="">Sin proveedor</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id} disabled={!s.is_active}>
                      {s.name}{!s.is_active ? ' (inactivo)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!editingLot && (
                  <div>
                    <label htmlFor="lot-requested-date" className="block text-sm font-medium text-espresso-700 mb-1">
                      Fecha de solicitud
                    </label>
                    <input
                      id="lot-requested-date"
                      type="date"
                      name="requested_date"
                      value={form.requested_date}
                      onChange={handleChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="lot-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                    Llegada estimada
                  </label>
                  <input
                    id="lot-eta"
                    type="date"
                    name="expected_arrival_date"
                    value={form.expected_arrival_date}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              {!editingLot && (
                <div>
                  <label htmlFor="lot-status" className="block text-sm font-medium text-espresso-700 mb-1">
                    Estado inicial
                  </label>
                  <select
                    id="lot-status"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_transit">En Tránsito</option>
                  </select>
                </div>
              )}
              <div>
                <label htmlFor="lot-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="lot-notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingLot ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {receiveLot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Recibir Lote {receiveLot.lot_code}</h2>
              <p className="text-sm text-espresso-500 mt-1">
                Confirma que el lote llegó físicamente a {receiveLot.warehouse_name}.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label htmlFor="received-date" className="block text-sm font-medium text-espresso-700 mb-1">
                  Fecha de recepción
                </label>
                <input
                  id="received-date"
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setReceiveLot(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReceiveSubmit}
                  disabled={receiveSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {receiveSaving ? 'Guardando...' : 'Confirmar Recepción'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
